import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { authAPI, elifbaAPI } from '../utils/api';
import { Volume2, ChevronLeft, CheckCircle2, BookOpen, Sparkles, Type, Layers, Hash, FileText, Languages, Play } from 'lucide-react';
import { toast } from 'sonner';
import { speakArabic, ttsAvailable } from '../utils/tts';

// Turkish Elif-Ba letter order matches Diyanet's btn_1..btn_28 ordering
const LETTERS = [
  { id: 1,  ar: 'ا',  name: 'Elif',  btn: 1  },
  { id: 2,  ar: 'ب',  name: 'Be',    btn: 2  },
  { id: 3,  ar: 'ت',  name: 'Te',    btn: 3  },
  { id: 4,  ar: 'ث',  name: 'Se',    btn: 4  },
  { id: 5,  ar: 'ج',  name: 'Cim',   btn: 5  },
  { id: 6,  ar: 'ح',  name: 'Ha',    btn: 6  },
  { id: 7,  ar: 'خ',  name: 'Hı',    btn: 7  },
  { id: 8,  ar: 'د',  name: 'Dal',   btn: 8  },
  { id: 9,  ar: 'ذ',  name: 'Zel',   btn: 9  },
  { id: 10, ar: 'ر',  name: 'Ra',    btn: 10 },
  { id: 11, ar: 'ز',  name: 'Ze',    btn: 11 },
  { id: 12, ar: 'س',  name: 'Sin',   btn: 12 },
  { id: 13, ar: 'ش',  name: 'Şın',   btn: 13 },
  { id: 14, ar: 'ص',  name: 'Sad',   btn: 14 },
  { id: 15, ar: 'ض',  name: 'Dad',   btn: 15 },
  { id: 16, ar: 'ط',  name: 'Tı',    btn: 16 },
  { id: 17, ar: 'ظ',  name: 'Zı',    btn: 17 },
  { id: 18, ar: 'ع',  name: 'Ayın',  btn: 18 },
  { id: 19, ar: 'غ',  name: 'Gayın', btn: 19 },
  { id: 20, ar: 'ف',  name: 'Fe',    btn: 20 },
  { id: 21, ar: 'ق',  name: 'Kaf',   btn: 21 },
  { id: 22, ar: 'ك',  name: 'Kef',   btn: 22 },
  { id: 23, ar: 'ل',  name: 'Lam',   btn: 23 },
  { id: 24, ar: 'م',  name: 'Mim',   btn: 24 },
  { id: 25, ar: 'ن',  name: 'Nun',   btn: 25 },
  { id: 26, ar: 'و',  name: 'Vav',   btn: 26 },
  { id: 27, ar: 'ه',  name: 'He',    btn: 27 },
  { id: 28, ar: 'ي',  name: 'Ye',    btn: 28 },
];

const LESSONS = [
  {
    id: 'isimleri',
    title: 'Harflerin İsimleri',
    subtitle: 'Elif, Be, Te... her harfin Türkçe adı',
    icon: Type,
    desc: 'Arapça harflerin Türkçe okunuşlarını öğren.',
  },
  {
    id: 'sesleri',
    title: 'Harflerin Sesleri',
    subtitle: 'Her harfin Kuran\'daki sesi',
    icon: Volume2,
    desc: 'Her harfin Arapça çıkış sesini öğren (mahreç).',
  },
  {
    id: 'sekilleri',
    title: 'Harflerin Şekilleri',
    subtitle: 'Başta, ortada, sonda farklı yazılışlar',
    icon: Layers,
    desc: 'Harfin başta, ortada, sonda nasıl yazıldığını gör.',
  },
  {
    id: 'pekistirme',
    title: 'Pekiştirme',
    subtitle: 'Öğrendiklerini tekrar et',
    icon: Sparkles,
    desc: 'Tüm harfleri karışık dinleyerek pekiştir.',
  },
];

const ElifBa = () => {
  const { isKidsMode } = useMode();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeModule, setActiveModule] = useState(null); // 'harekeler' | 'kelimeler' | 'kisa-sureler'
  const [playingBtn, setPlayingBtn] = useState(null);
  const [visitedStages, setVisitedStages] = useState(() => {
    try {
      const raw = localStorage.getItem('elifba_visited_stages');
      return raw ? JSON.parse(raw) : { stage2: false, stage3: false };
    } catch { return { stage2: false, stage3: false }; }
  });
  const audioRef = useRef(null);
  const preloadCache = useRef({});

  const markStageVisited = (key) => {
    setVisitedStages((prev) => {
      if (prev[key]) return prev;
      const next = { ...prev, [key]: true };
      try { localStorage.setItem('elifba_visited_stages', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Stage 2 (Harekeler) unlocks after all 28 letters have been completed at
  // least once in stage 1. Stage 3 unlocks after the user opens Harekeler.
  // Stage 4 unlocks after the user opens Kelimeler.
  const stage1Done = completed.length >= LETTERS.length;
  const stage2Unlocked = stage1Done;
  const stage3Unlocked = stage2Unlocked && visitedStages.stage2;
  const stage4Unlocked = stage3Unlocked && visitedStages.stage3;

  useEffect(() => {
    (async () => {
      try {
        const u = await authAPI.getMe();
        setUser(u.data);
        const p = await elifbaAPI.getProgress();
        setCompleted(p.data?.completed_letters || []);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  // Preload all 28 letter audios when a lesson is opened
  useEffect(() => {
    if (!activeLesson) return;
    preloadCache.current = {};
    const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL);
    LETTERS.forEach((letter) => {
      const url = `${base}/api/elifba/diyanet-audio/${activeLesson}/btn_${letter.btn}?raw=1`;
      const a = new Audio();
      a.preload = 'auto';
      a.src = url;
      preloadCache.current[letter.btn] = a;
    });
    return () => {
      // cleanup on lesson change
      Object.values(preloadCache.current).forEach((a) => {
        try { a.pause(); a.src = ''; } catch (_) { /* ignore */ }
      });
      preloadCache.current = {};
    };
  }, [activeLesson]);

  const accent = isKidsMode ? 'amber' : 'teal';

  const playLetter = async (btn) => {
    try {
      const preloaded = preloadCache.current?.[btn];
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      }
      const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL);
      const nextAudio = preloaded || new Audio(`${base}/api/elifba/diyanet-audio/${activeLesson}/btn_${btn}?raw=1`);
      if (preloaded) {
        try { nextAudio.currentTime = 0; } catch (_) { /* ignore */ }
      }
      setPlayingBtn(btn);
      const handleEnd = () => setPlayingBtn(null);
      const handleError = () => { setPlayingBtn(null); toast.error('Ses oynatılamadı'); };
      nextAudio.addEventListener('ended', handleEnd, { once: true });
      nextAudio.addEventListener('error', handleError, { once: true });
      audioRef.current = nextAudio;
      await nextAudio.play();
    } catch (e) {
      setPlayingBtn(null);
      toast.error('Ses oynatılamadı');
    }
  };

  const markComplete = async (letterId) => {
    if (completed.includes(letterId)) return;
    try {
      await elifbaAPI.markCompleted(letterId);
      setCompleted((prev) => [...prev, letterId]);
    } catch {
      // ignore
    }
  };

  if (activeModule) {
    // Mark which stage was visited so the next stage unlocks for the user.
    if (activeModule === 'harekeler') markStageVisited('stage2');
    if (activeModule === 'kelimeler') markStageVisited('stage3');
    return <CurriculumModule moduleId={activeModule} onBack={() => setActiveModule(null)} user={user} isKidsMode={isKidsMode} navigate={navigate} />;
  }

  if (!activeLesson) {
    // Lesson chooser
    return (
      <AppShell user={user} title="Elif-Ba">
        <div className="max-w-5xl mx-auto p-4 sm:p-8 md:p-12 space-y-8">
          <header className="mb-6">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Çocuk Modu</p>
            <h1
              className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Elif-Ba
            </h1>
            <p className="text-stone-600 mt-2 max-w-2xl">
              Kuran-ı Kerim&apos;in temellerini öğrenmeye buradan başla. Diyanet İşleri Başkanlığı
              müfredatına göre hazırlanmış, profesyonel ses kayıtlarıyla.
            </p>
          </header>

          {/* Stage 1: Diyanet 4 lessons */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-full ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center text-sm font-bold`} style={{ fontFamily: 'Outfit, sans-serif' }}>1</div>
              <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Harfleri Öğren
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {LESSONS.map((l) => {
                const Icon = l.icon;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setActiveLesson(l.id)}
                    data-testid={`elifba-lesson-${l.id}`}
                    className="group text-left p-7 bg-white rounded-3xl border border-stone-200 hover:border-stone-300 hover:shadow-lg transition-all"
                  >
                    <div
                      className={`w-14 h-14 rounded-2xl ${
                        isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'
                      } flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3
                      className="text-xl font-bold text-stone-900 mb-1"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {l.title}
                    </h3>
                    <p className="text-sm text-stone-500 mb-2">{l.subtitle}</p>
                    <p className="text-xs text-stone-400 leading-relaxed">{l.desc}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Stage 2: Harekeler */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-full ${stage2Unlocked ? (isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700') : 'bg-stone-100 text-stone-400'} flex items-center justify-center text-sm font-bold`} style={{ fontFamily: 'Outfit, sans-serif' }}>2</div>
              <h2 className={`text-lg font-semibold ${stage2Unlocked ? 'text-stone-900' : 'text-stone-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                Harekeleri Öğren
              </h2>
              {!stage2Unlocked && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 font-semibold" data-testid="stage2-locked-badge">🔒 Önce tüm harfleri tamamla</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => stage2Unlocked && setActiveModule('harekeler')}
              disabled={!stage2Unlocked}
              data-testid="elifba-module-harekeler"
              className={`w-full text-left p-7 rounded-3xl border transition-all flex items-center gap-5 ${stage2Unlocked ? 'bg-white border-stone-200 hover:border-stone-300 hover:shadow-lg' : 'bg-stone-50 border-stone-200 opacity-60 cursor-not-allowed'}`}
            >
              <div className={`w-14 h-14 rounded-2xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center flex-shrink-0`}>
                <Hash className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Harekeler (Sesli İşaretler)</h3>
                <p className="text-sm text-stone-500">Üstün, esre, ötre, cezm, şedde, tenvin — harflerin nasıl seslendirildiği.</p>
              </div>
              <div className="hidden sm:flex flex-wrap gap-1.5">
                {['َ', 'ِ', 'ُ', 'ْ', 'ّ'].map((s, i) => (
                  <span key={i} className="text-3xl text-stone-700" style={{ fontFamily: 'Amiri, serif' }}>{s}</span>
                ))}
              </div>
            </button>
          </section>

          {/* Stage 3: Kelimeler */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-full ${stage3Unlocked ? (isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700') : 'bg-stone-100 text-stone-400'} flex items-center justify-center text-sm font-bold`} style={{ fontFamily: 'Outfit, sans-serif' }}>3</div>
              <h2 className={`text-lg font-semibold ${stage3Unlocked ? 'text-stone-900' : 'text-stone-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                Kelimeleri Öğren
              </h2>
              {!stage3Unlocked && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 font-semibold" data-testid="stage3-locked-badge">🔒 Önce harekeleri öğren</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => stage3Unlocked && setActiveModule('kelimeler')}
              disabled={!stage3Unlocked}
              data-testid="elifba-module-kelimeler"
              className={`w-full text-left p-7 rounded-3xl border transition-all flex items-center gap-5 ${stage3Unlocked ? 'bg-white border-stone-200 hover:border-stone-300 hover:shadow-lg' : 'bg-stone-50 border-stone-200 opacity-60 cursor-not-allowed'}`}
            >
              <div className={`w-14 h-14 rounded-2xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center flex-shrink-0`}>
                <Languages className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Temel Kelimeler</h3>
                <p className="text-sm text-stone-500">Allah, Rahman, Rahim... Fatiha&apos;da geçen kelimelerin anlamı.</p>
              </div>
              <span className="text-2xl text-stone-700 hidden sm:inline" style={{ fontFamily: 'Amiri, serif' }}>ٱللَّٰهُ</span>
            </button>
          </section>

          {/* Stage 4: Kısa Sureler */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-full ${stage4Unlocked ? (isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700') : 'bg-stone-100 text-stone-400'} flex items-center justify-center text-sm font-bold`} style={{ fontFamily: 'Outfit, sans-serif' }}>4</div>
              <h2 className={`text-lg font-semibold ${stage4Unlocked ? 'text-stone-900' : 'text-stone-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                Kısa Sureleri Öğren
              </h2>
              {!stage4Unlocked && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 font-semibold" data-testid="stage4-locked-badge">🔒 Önce kelimeleri öğren</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => stage4Unlocked && setActiveModule('kisa-sureler')}
              disabled={!stage4Unlocked}
              data-testid="elifba-module-kisa-sureler"
              className={`w-full text-left p-7 rounded-3xl border transition-all flex items-center gap-5 ${stage4Unlocked ? 'bg-white border-stone-200 hover:border-stone-300 hover:shadow-lg' : 'bg-stone-50 border-stone-200 opacity-60 cursor-not-allowed'}`}
            >
              <div className={`w-14 h-14 rounded-2xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center flex-shrink-0`}>
                <FileText className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Kısa Sureler</h3>
                <p className="text-sm text-stone-500">Fatiha, İhlas, Felak, Nas, Kevser, Nasr — ezberlemeye buradan başla.</p>
              </div>
              <BookOpen className="w-6 h-6 text-stone-400 hidden sm:block" />
            </button>
          </section>

          <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 text-xs text-stone-500 leading-relaxed">
            <strong className="text-stone-700">Ses Kaynağı:</strong> Bu derslerdeki ses kayıtları,
            Diyanet İşleri Başkanlığı&apos;nın resmi Elif-Ba eğitim müfredatından ilham alınarak
            hazırlanmıştır.
          </div>
        </div>
      </AppShell>
    );
  }

  // Letter grid for chosen lesson
  const lesson = LESSONS.find((l) => l.id === activeLesson);

  return (
    <AppShell user={user} title={lesson.title}>
      <div className="max-w-6xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <button
          type="button"
          onClick={() => setActiveLesson(null)}
          data-testid="elifba-back"
          className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900"
        >
          <ChevronLeft className="w-4 h-4" /> Ders seçimine dön
        </button>

        <header className="pb-4 border-b border-stone-200">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">
            Elif-Ba · {lesson.subtitle}
          </p>
          <h1
            className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {lesson.title}
          </h1>
          <p className="text-stone-600 mt-2 text-sm">{lesson.desc}</p>
        </header>

        <div dir="rtl" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-3 sm:gap-4">
          {LETTERS.map((letter, idx) => {
            const isPlaying = playingBtn === letter.btn;
            const isDone = completed.includes(letter.id);
            // Sequential unlock: letter N is unlocked when N-1 is in completed[]
            const prevDone = idx === 0 || completed.includes(LETTERS[idx - 1].id);
            const isLocked = !isDone && !prevDone;
            return (
              <button
                key={letter.id}
                type="button"
                dir="ltr"
                disabled={isLocked}
                onClick={async () => {
                  if (isLocked) {
                    toast.info(`Önce ${LETTERS[idx - 1].name} harfini tamamla`);
                    return;
                  }
                  await playLetter(letter.btn);
                  markComplete(letter.id);
                }}
                data-testid={`elifba-letter-${letter.id}`}
                className={`relative aspect-square rounded-2xl border-2 transition-all flex flex-col items-center justify-center p-3 ${
                  isLocked
                    ? 'bg-stone-50 border-stone-200 text-stone-300 cursor-not-allowed opacity-60'
                    : isPlaying
                    ? isKidsMode
                      ? 'bg-amber-500 border-amber-600 text-white scale-105 shadow-lg'
                      : 'bg-teal-700 border-teal-800 text-white scale-105 shadow-lg'
                    : 'bg-white border-stone-200 hover:border-stone-400 text-stone-900 hover:shadow-md'
                }`}
              >
                {isLocked && (
                  <span className="absolute top-2 right-2 text-stone-400 text-sm" data-testid={`elifba-letter-locked-${letter.id}`}>🔒</span>
                )}
                {isDone && !isLocked && (
                  <CheckCircle2
                    className={`absolute top-2 right-2 w-4 h-4 ${
                      isPlaying ? 'text-white' : isKidsMode ? 'text-amber-600' : 'text-teal-600'
                    }`}
                  />
                )}
                <div
                  className="text-4xl sm:text-5xl mb-1"
                  style={{ fontFamily: 'Amiri, "Noto Naskh Arabic", serif', lineHeight: 1 }}
                >
                  {letter.ar}
                </div>
                <div className="text-xs sm:text-sm font-medium opacity-80">{letter.name}</div>
                <Volume2 className={`absolute bottom-2 right-2 w-3 h-3 ${isPlaying ? 'opacity-100' : 'opacity-30'}`} />
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl p-5 border border-stone-200 text-sm text-stone-600">
          <strong className="text-stone-800">İlerleme:</strong>{' '}
          <span className={isKidsMode ? 'text-amber-700' : 'text-teal-700'}>
            {completed.length} / {LETTERS.length}
          </span>{' '}
          harf çalışıldı. Tüm harflere dokunup sesini dinlediğinde tamamlanmış sayılır.
        </div>
      </div>
    </AppShell>
  );
};

export default ElifBa;

// =============================================================
// Curriculum Module: Harekeler, Kelimeler, Kisa Sureler
// =============================================================
const CurriculumModule = ({ moduleId, onBack, user, isKidsMode, navigate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fn = moduleId === 'harekeler' ? elifbaAPI.getHarekeler
      : moduleId === 'kelimeler' ? elifbaAPI.getKelimeler
      : elifbaAPI.getKisaSureler;
    fn()
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error('Yüklenemedi'))
      .finally(() => setLoading(false));
  }, [moduleId]);

  const titles = {
    harekeler: { tr: 'Harekeler', sub: 'Üstün, esre, ötre ve diğer sesli işaretler' },
    kelimeler: { tr: 'Temel Kelimeler', sub: 'Fatiha ve günlük dualarda geçen kelimeler' },
    'kisa-sureler': { tr: 'Kısa Sureler', sub: 'Ezberlemeye buradan başla' },
  };
  const t = titles[moduleId] || { tr: '', sub: '' };

  return (
    <AppShell user={user} title={t.tr}>
      <div className="max-w-5xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <button type="button" onClick={onBack} data-testid="curriculum-back" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
          <ChevronLeft className="w-4 h-4" /> Ders seçimine dön
        </button>
        <header className="pb-4 border-b border-stone-200">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Elif-Ba · İleri Müfredat</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.tr}</h1>
          <p className="text-stone-600 mt-2 text-sm">{t.sub}</p>
        </header>

        {loading ? (
          <div className="text-center py-12 text-stone-500">Yükleniyor...</div>
        ) : moduleId === 'harekeler' ? (
          <HarekelerView items={items} isKidsMode={isKidsMode} />
        ) : moduleId === 'kelimeler' ? (
          <KelimelerView items={items} isKidsMode={isKidsMode} />
        ) : (
          <KisaSurelerView items={items} isKidsMode={isKidsMode} navigate={navigate} />
        )}
      </div>
    </AppShell>
  );
};

const HarekelerView = ({ items, isKidsMode }) => {
  const speak = (text) => {
    if (!ttsAvailable()) {
      toast.error('Tarayıcınız sesli okumayı desteklemiyor');
      return;
    }
    speakArabic(text, { rate: 0.75, onError: () => toast.error('Ses oynatılamadı') });
  };
  return (
    <div className="space-y-5">
      {items.map((h) => (
        <div key={h.id} className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-7" data-testid={`hareke-${h.id}`}>
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 rounded-2xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-800'} flex items-center justify-center`}>
              <span className="text-4xl" style={{ fontFamily: 'Amiri, serif' }}>{h.symbol}</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{h.name_tr}</h3>
              <p className="text-sm text-stone-500">{h.description_tr}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${isKidsMode ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-teal-700'}`}>
              {h.sound}
            </span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 pt-4 border-t border-stone-100" dir="rtl">
            {h.examples.map((ex, i) => (
              <button
                type="button"
                key={i}
                onClick={() => speak(ex.ar)}
                data-testid={`hareke-${h.id}-ex-${i}`}
                className="aspect-square rounded-xl bg-stone-50 border border-stone-200 hover:border-amber-300 hover:bg-amber-50 flex flex-col items-center justify-center relative group transition-all"
                dir="ltr"
                title="Dinlemek için tıkla"
              >
                <span className="text-3xl text-stone-900" style={{ fontFamily: 'Amiri, serif' }}>{ex.ar}</span>
                <span className="text-xs text-stone-500 mt-1">{ex.tr}</span>
                <Volume2 className="w-3 h-3 text-stone-400 absolute top-1.5 right-1.5 group-hover:text-amber-600" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const KelimelerView = ({ items, isKidsMode }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {items.map((k) => (
      <div key={k.id} className="bg-white rounded-2xl border border-stone-200 p-5 hover:shadow-md transition-all" data-testid={`kelime-${k.id}`}>
        <div className="flex items-start justify-between mb-3">
          <span className="text-3xl text-stone-900" style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}>{k.ar}</span>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isKidsMode ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-teal-800'}`}>{k.tr}</span>
        </div>
        <p className="text-sm text-stone-700">{k.meaning_tr}</p>
        <p className="text-xs text-stone-400 mt-1">{k.en}</p>
      </div>
    ))}
  </div>
);

const KisaSurelerView = ({ items, isKidsMode, navigate }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {items.map((s) => (
      <button
        key={s.id}
        type="button"
        onClick={() => navigate(`/oku/${s.surah}`)}
        data-testid={`kisa-sure-${s.id}`}
        className="text-left bg-white rounded-2xl border border-stone-200 p-5 hover:shadow-md hover:border-stone-300 transition-all"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-800'} flex items-center justify-center font-bold text-sm`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              {s.surah}
            </div>
            <div>
              <p className="font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.name_tr}</p>
              <p className="text-xs text-stone-500">{s.ayah_count} ayet</p>
            </div>
          </div>
          <span className="text-2xl text-stone-700" style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}>{s.name_ar}</span>
        </div>
        <p className="text-xs text-stone-600">{s.description_tr}</p>
        <p className={`text-xs font-semibold mt-3 ${isKidsMode ? 'text-amber-700' : 'text-teal-700'}`}>
          Oku & Dinle →
        </p>
      </button>
    ))}
  </div>
);
